import { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { useAuth, useIsAdmin, useIsDirector, useIsManagement } from '@/hooks/useAuth';
import { ToastProvider } from '@/components/ui/Toast';
import { BrandSplash } from '@/components/ui/BrandMark';

import { AppShell } from '@/components/layout/AppShell';
import { ErrorPage } from '@/components/ErrorPage';
import { lazyPage } from '@/lib/lazyPage';

// Eager: the two screens everyone hits immediately after opening the app.
import { LoginPage } from '@/pages/Login';
import { DashboardPage } from '@/pages/Dashboard';

// Every other page is its own chunk, fetched on first visit (see lazyPage).
const inventoryListPage   = lazyPage(() => import('@/pages/Inventory/InventoryListPage').then(m => ({ default: m.InventoryListPage })));
const inventoryDetailPage = lazyPage(() => import('@/pages/Inventory/InventoryDetailPage').then(m => ({ default: m.InventoryDetailPage })));
const unitHistoryPage     = lazyPage(() => import('@/pages/Inventory/UnitHistoryPage').then(m => ({ default: m.UnitHistoryPage })));
const jobListPage         = lazyPage(() => import('@/pages/Jobs/JobListPage').then(m => ({ default: m.JobListPage })));
const newJobPage          = lazyPage(() => import('@/pages/Jobs/NewJobPage').then(m => ({ default: m.NewJobPage })));
const jobDetailPage       = lazyPage(() => import('@/pages/Jobs/JobDetailPage').then(m => ({ default: m.JobDetailPage })));
const jobReviewPage       = lazyPage(() => import('@/pages/Jobs/JobReviewPage').then(m => ({ default: m.JobReviewPage })));
const clientsPage         = lazyPage(() => import('@/pages/Clients/ClientsPage').then(m => ({ default: m.ClientsPage })));
const labourCataloguePage    = lazyPage(() => import('@/pages/Catalogues/LabourCataloguePage').then(m => ({ default: m.LabourCataloguePage })));
const transportCataloguePage = lazyPage(() => import('@/pages/Catalogues/TransportCataloguePage').then(m => ({ default: m.TransportCataloguePage })));
const otherCataloguePage     = lazyPage(() => import('@/pages/Catalogues/OtherCataloguePage').then(m => ({ default: m.OtherCataloguePage })));
const salesCataloguePage     = lazyPage(() => import('@/pages/Catalogues/SalesCataloguePage').then(m => ({ default: m.SalesCataloguePage })));
const subHireCataloguePage   = lazyPage(() => import('@/pages/Catalogues/SubHireCataloguePage').then(m => ({ default: m.SubHireCataloguePage })));
const supplierDetailPage     = lazyPage(() => import('@/pages/Catalogues/SupplierDetailPage').then(m => ({ default: m.SupplierDetailPage })));
const crewCataloguePage      = lazyPage(() => import('@/pages/Catalogues/CrewCataloguePage').then(m => ({ default: m.CrewCataloguePage })));
const crewDetailPage         = lazyPage(() => import('@/pages/Catalogues/CrewDetailPage').then(m => ({ default: m.CrewDetailPage })));
const usersPage       = lazyPage(() => import('@/pages/Admin/UsersPage').then(m => ({ default: m.UsersPage })));
const categoriesPage  = lazyPage(() => import('@/pages/Admin/CategoriesPage').then(m => ({ default: m.CategoriesPage })));
const systemPage      = lazyPage(() => import('@/pages/Admin/SystemPage').then(m => ({ default: m.SystemPage })));
const dataPage        = lazyPage(() => import('@/pages/Admin/DataPage').then(m => ({ default: m.DataPage })));
const activityLogPage = lazyPage(() => import('@/pages/Admin/ActivityLogPage').then(m => ({ default: m.ActivityLogPage })));
const analyticsPage   = lazyPage(() => import('@/pages/Admin/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const devToolsPage    = lazyPage(() => import('@/pages/Admin/DevToolsPage').then(m => ({ default: m.DevToolsPage })));
const testTagPage        = lazyPage(() => import('@/pages/Admin/TestTagPage').then(m => ({ default: m.TestTagPage })));
const testTagHistoryPage = lazyPage(() => import('@/pages/Admin/TestTagHistoryPage').then(m => ({ default: m.TestTagHistoryPage })));
const testTagFaultyPage  = lazyPage(() => import('@/pages/Admin/TestTagFaultyPage').then(m => ({ default: m.TestTagFaultyPage })));
const calendarPage      = lazyPage(() => import('@/pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const notificationsPage = lazyPage(() => import('@/pages/Settings/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const warehousePage     = lazyPage(() => import('@/pages/Warehouse/WarehousePage').then(m => ({ default: m.WarehousePage })));
const warehouseJobPage  = lazyPage(() => import('@/pages/Warehouse/WarehouseJobPage').then(m => ({ default: m.WarehouseJobPage })));
const projectListPage   = lazyPage(() => import('@/pages/Projects/ProjectListPage').then(m => ({ default: m.ProjectListPage })));
const newProjectPage    = lazyPage(() => import('@/pages/Projects/NewProjectPage').then(m => ({ default: m.NewProjectPage })));
const projectDetailPage = lazyPage(() => import('@/pages/Projects/ProjectDetailPage').then(m => ({ default: m.ProjectDetailPage })));

// Keep the launch splash up long enough for the mark to finish drawing itself
// in, even when the auth check resolves instantly. Timed from when the module
// first loads (i.e. a full page load), so it fires once per hard reload and
// never delays in-app navigation between protected routes.
const SPLASH_MIN_MS = 1700;
const appLoadedAt = Date.now();

function useMinimumSplash(): boolean {
  const [done, setDone] = useState(() => Date.now() - appLoadedAt >= SPLASH_MIN_MS);
  useEffect(() => {
    if (done) return;
    const remaining = SPLASH_MIN_MS - (Date.now() - appLoadedAt);
    const t = setTimeout(() => setDone(true), Math.max(0, remaining));
    return () => clearTimeout(t);
  }, [done]);
  return done;
}

/** Wraps routes that require authentication */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const minSplashDone = useMinimumSplash();
  if (isLoading || !minSplashDone) return <BrandSplash />;
  if (!user)     return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Wraps routes that require admin role */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Wraps routes that require director or admin role */
function RequireDirector({ children }: { children: React.ReactNode }) {
  const isDirector = useIsDirector();
  if (!isDirector) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Wraps routes that require management, director, or admin role (blocks staff) */
function RequireManagement({ children }: { children: React.ReactNode }) {
  const isManagement = useIsManagement();
  if (!isManagement) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage />, handle: { title: 'Dashboard' } },
      { path: 'calendar', element: calendarPage, handle: { title: 'Calendar' } },

      // Per-user settings — available to every signed-in user
      { path: 'settings/notifications', element: notificationsPage, handle: { title: 'Notification Settings' } },

      // Warehouse
      { path: 'warehouse',        element: warehousePage,    handle: { title: 'Warehouse' } },
      { path: 'warehouse/:jobId', element: warehouseJobPage, handle: { title: 'Warehouse - Job' } },

      // Inventory
      { path: 'inventory',       element: inventoryListPage,   handle: { title: 'Equipment Inventory' } },
      // More-specific route first so /inventory/units/:assetId doesn't get
      // swallowed by /inventory/:id.
      { path: 'inventory/units/:assetId', element: unitHistoryPage, handle: { title: 'Asset history' } },
      { path: 'inventory/:id',   element: inventoryDetailPage, handle: { title: 'Equipment Detail' } },

      // Jobs
      { path: 'jobs',            element: jobListPage,  handle: { title: 'Jobs' } },
      { path: 'jobs/new',        element: newJobPage,   handle: { title: 'New Job' } },
      { path: 'jobs/:id',        element: jobDetailPage, handle: { title: 'Job' } },
      { path: 'jobs/:id/review', element: <RequireManagement>{jobReviewPage}</RequireManagement>, handle: { title: 'Post-Job Review' } },

      // Projects — management only
      { path: 'projects',        element: <RequireManagement>{projectListPage}</RequireManagement>,   handle: { title: 'Projects' } },
      { path: 'projects/new',    element: <RequireManagement>{newProjectPage}</RequireManagement>,    handle: { title: 'New Project' } },
      { path: 'projects/:id',    element: <RequireManagement>{projectDetailPage}</RequireManagement>, handle: { title: 'Project' } },

      // Clients — management and admin only
      { path: 'clients',         element: <RequireManagement>{clientsPage}</RequireManagement>,  handle: { title: 'Clients' } },

      // Catalogues
      { path: 'catalogue/labour',    element: labourCataloguePage,    handle: { title: 'Labour Catalogue' } },
      { path: 'catalogue/transport', element: transportCataloguePage, handle: { title: 'Transport Catalogue' } },
      { path: 'catalogue/other', element: otherCataloguePage, handle: { title: 'Other Catalogue' } },
      { path: 'catalogue/sales', element: salesCataloguePage, handle: { title: 'Sales Catalogue' } },
      { path: 'catalogue/crew',    element: <RequireManagement>{crewCataloguePage}</RequireManagement>, handle: { title: 'Crew' } },
      { path: 'catalogue/crew/:id', element: <RequireManagement>{crewDetailPage}</RequireManagement>, handle: { title: 'Crew Detail' } },
      { path: 'catalogue/subhire', element: subHireCataloguePage, handle: { title: 'Sub-Hire Catalogue' } },
      { path: 'catalogue/subhire/supplier/:id', element: supplierDetailPage, handle: { title: 'Supplier Detail' } },

      // Director + Admin
      { path: 'admin/categories', element: <RequireDirector>{categoriesPage}</RequireDirector>, handle: { title: 'Categories' } },
      { path: 'admin/data',       element: <RequireDirector>{dataPage}</RequireDirector>,       handle: { title: 'Data Management' } },
      // Management+: the sales tab is theirs. The equipment tab inside renders
      // for directors only, and /analytics/stock stays requireDirector.
      { path: 'admin/analytics',  element: <RequireManagement>{analyticsPage}</RequireManagement>,  handle: { title: 'Analytics' } },

      // Admin only
      { path: 'admin/users',      element: <RequireAdmin>{usersPage}</RequireAdmin>,      handle: { title: 'Users' } },
      { path: 'admin/system',     element: <RequireAdmin>{systemPage}</RequireAdmin>,     handle: { title: 'System' } },
      { path: 'admin/activity-log', element: <RequireAdmin>{activityLogPage}</RequireAdmin>, handle: { title: 'Activity Log' } },
      { path: 'admin/dev-tools',    element: <RequireAdmin>{devToolsPage}</RequireAdmin>,    handle: { title: 'Dev Tools' } },

      // Test & Tag — available to all signed-in users
      { path: 'admin/test-tag',         element: testTagPage,        handle: { title: 'Test & Tag' } },
      { path: 'admin/test-tag/history', element: testTagHistoryPage, handle: { title: 'Test & Tag History' } },
      { path: 'admin/test-tag/faulty',  element: testTagFaultyPage,  handle: { title: 'Faulty Equipment' } },
    ],
  },
]);

export default function App() {
  // Quality-of-life: when any number field (quantity, days, price, etc.) gains
  // focus, select its contents so typing replaces the old value instead of
  // appending to it. Common spreadsheet/quoting UX. We listen for `focusin`
  // (which bubbles, unlike `focus`) once at the document level so this applies
  // to every <input type="number"> in the app without touching each one — and
  // because we don't change the input type, the up/down spinner arrows stay.
  useEffect(() => {
    const selectOnFocus = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (t instanceof HTMLInputElement && t.type === 'number') {
        // Defer so the browser's own focus handling (incl. spinner clicks)
        // settles before we select; otherwise some browsers clear it again.
        requestAnimationFrame(() => {
          try { t.select(); } catch { /* not all inputs support select() */ }
        });
      }
    };
    document.addEventListener('focusin', selectOnFocus);
    return () => document.removeEventListener('focusin', selectOnFocus);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  );
}
